import React, { Component } from 'react';
import { Modal, Button } from 'react-bootstrap';
import moment from 'moment';
import { editSession } from '../../../services/timeTrackingService';
import { convertFromUTC } from '../../../util/convert_timezone';

// Edit session reason codes
const EDIT_REASONS = [
  { code: 'time_correction', label: 'Time correction' },
  { code: 'missed_punch', label: 'Missed punch' },
  { code: 'system_error', label: 'System error' },
  { code: 'other', label: 'Other' }
];

/**
 * EditSessionModal - Modal for editing session times
 *
 * Props:
 * - show: Boolean - whether modal is visible
 * - onHide: Function - called when modal is closed
 * - onSuccess: Function - called after successful edit
 * - session: Object - the session being edited
 * - actorUserId: Number - the manager's user ID performing the action
 */
class EditSessionModal extends Component {
  state = {
    checkInAt: '',
    checkOutAt: '',
    reasonCode: '',
    note: '',
    loading: false,
    error: null
  };

  componentDidUpdate(prevProps) {
    // Reset state when modal opens with new session
    if (this.props.show && !prevProps.show && this.props.session) {
      const { session } = this.props;
      this.setState({
        checkInAt: session.check_in_at ? convertFromUTC(session.check_in_at).format('YYYY-MM-DDTHH:mm') : '',
        checkOutAt: session.check_out_at ? convertFromUTC(session.check_out_at).format('YYYY-MM-DDTHH:mm') : '',
        reasonCode: '',
        note: '',
        loading: false,
        error: null
      });
    }
  }

  handleInputChange = (field, value) => {
    this.setState({ [field]: value, error: null });
  };

  handleSubmit = async () => {
    const { session, onSuccess, onHide, actorUserId } = this.props;
    const { checkInAt, checkOutAt, reasonCode, note } = this.state;

    // Validation
    if (!checkInAt) {
      this.setState({ error: 'Check-in time is required.' });
      return;
    }

    if (!reasonCode) {
      this.setState({ error: 'Please select a reason for this edit.' });
      return;
    }

    if (!note.trim()) {
      this.setState({ error: 'Please provide a note explaining the edit.' });
      return;
    }

    // Validate check-out is after check-in
    if (checkOutAt) {
      const checkInMoment = moment(checkInAt);
      const checkOutMoment = moment(checkOutAt);
      if (checkOutMoment.isSameOrBefore(checkInMoment)) {
        this.setState({ error: 'Check-out time must be after check-in time.' });
        return;
      }
    }

    try {
      this.setState({ loading: true, error: null });

      // Convert to ISO strings for API
      const checkInIso = moment(checkInAt).toISOString();
      const checkOutIso = checkOutAt ? moment(checkOutAt).toISOString() : null;

      await editSession(session.id, checkInIso, checkOutIso, reasonCode, note, actorUserId);

      onHide();
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      console.error('Edit session error:', err);
      const errorMsg = err.response?.data?.message || 'Failed to update session. Please try again.';
      this.setState({ error: errorMsg, loading: false });
    }
  };

  // Format original times for reference display
  formatOriginalTime = (timeStr) => {
    if (!timeStr) return '—';
    return convertFromUTC(timeStr).format('MMM D, YYYY h:mm A');
  };

  render() {
    const { show, onHide, session } = this.props;
    const { checkInAt, checkOutAt, reasonCode, note, loading, error } = this.state;

    if (!session) return null;

    const isOpen = session.status === 'open';
    const sessionDate = session.check_in_at ? convertFromUTC(session.check_in_at).format('ddd, MMM D, YYYY') : 'Session';

    return (
      <Modal show={show} onHide={onHide} centered backdrop={loading ? 'static' : true} size="lg">
        <Modal.Header closeButton={!loading}>
          <Modal.Title>
            <i className="fa-sharp fa-regular fa-edit text-primary mr-2"></i>
            Edit Session - {sessionDate}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {/* Original Times Reference */}
          <div className="alert alert-light mb-4">
            <h6 className="mb-2">
              <i className="fa-sharp fa-regular fa-info-circle mr-1"></i>
              Original Times
            </h6>
            <div className="row">
              <div className="col-6">
                <small className="text-muted">Check-In:</small>
                <div>{this.formatOriginalTime(session.check_in_at)}</div>
              </div>
              <div className="col-6">
                <small className="text-muted">Check-Out:</small>
                <div>{this.formatOriginalTime(session.check_out_at)}</div>
              </div>
            </div>
          </div>

          {/* Check-In Time */}
          <div className="form-group">
            <label className="font-weight-bold">
              Check-In Time <span className="text-danger">*</span>
            </label>
            <input
              type="datetime-local"
              className="form-control"
              value={checkInAt}
              onChange={(e) => this.handleInputChange('checkInAt', e.target.value)}
              disabled={loading}
            />
          </div>

          {/* Check-Out Time */}
          <div className="form-group">
            <label className="font-weight-bold">
              Check-Out Time
              {isOpen && <span className="badge badge-info ml-2">Session is open</span>}
            </label>
            <input
              type="datetime-local"
              className="form-control"
              value={checkOutAt}
              onChange={(e) => this.handleInputChange('checkOutAt', e.target.value)}
              disabled={loading}
              placeholder={isOpen ? 'Leave empty if session is still open' : ''}
            />
            {isOpen && (
              <small className="form-text text-muted">
                Leave empty if the session should remain open, or set a time to close it.
              </small>
            )}
          </div>

          {/* Reason Selection */}
          <div className="form-group">
            <label className="font-weight-bold">
              Reason for Edit <span className="text-danger">*</span>
            </label>
            <select
              className="form-control"
              value={reasonCode}
              onChange={(e) => this.handleInputChange('reasonCode', e.target.value)}
              disabled={loading}
            >
              <option value="">Select a reason...</option>
              {EDIT_REASONS.map(reason => (
                <option key={reason.code} value={reason.code}>
                  {reason.label}
                </option>
              ))}
            </select>
          </div>

          {/* Note Field */}
          <div className="form-group">
            <label className="font-weight-bold">
              Note <span className="text-danger">*</span>
            </label>
            <textarea
              className="form-control"
              rows={3}
              value={note}
              onChange={(e) => this.handleInputChange('note', e.target.value)}
              placeholder="Explain why this edit was made..."
              disabled={loading}
            />
            <small className="form-text text-muted">
              This note will be recorded in the audit log.
            </small>
          </div>

          {/* Error Display */}
          {error && (
            <div className="alert alert-danger mb-0">
              <i className="fa-sharp fa-regular fa-exclamation-circle mr-2"></i>
              {error}
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={this.handleSubmit}
            disabled={loading || !checkInAt || !reasonCode || !note.trim()}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm mr-2"></span>
                Saving...
              </>
            ) : (
              <>
                <i className="fa-sharp fa-regular fa-save mr-2"></i>
                Save Changes
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
}

export default EditSessionModal;
